import { FilterProps } from 'components/table/enhancements/filters/filterTypes'
import {
  removeFilter,
  useClickOutside,
} from 'components/table/enhancements/filters/filterUtils'
import { FilterHeaderButton } from 'components/table/utility/FilterHeaderButton'
import { RemoveFilterButton } from 'components/table/utility/RemoveFilterButton'
import { format, parseISO } from 'date-fns'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  FilterContainer,
  LabelAndInputContainer,
  RelativeWrap,
  StyledCloseIcon,
  StyledInput,
  StyledLabel,
} from 'styles/assetDatabaseStyles'

const DateRangeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
`

const ALL_OPTION_VALUE = 'All'

function formatDateString(dateString: string | undefined | null): string {
  if (!dateString) return ''

  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number)
      const date = new Date(year, month - 1, day) // month is 0-indexed
      return format(date, 'MMM dd yyyy')
    }

    const date = parseISO(dateString)
    if (isNaN(date.getTime())) {
      console.error('Invalid date:', dateString)
      return dateString
    }
    return format(date, 'MMM dd yyyy')
  } catch (error) {
    console.error('Error formatting date:', error)
    return dateString
  }
}

function convertToDateInputValue(isoString: string | undefined | null): string {
  if (!isoString) return ''

  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(isoString)) {
      return isoString
    }

    // If it's an ISO string, extract the date part
    if (isoString.includes('T')) {
      return isoString.split('T')[0]
    }

    // Try to parse as date and format
    const date = parseISO(isoString)
    if (!isNaN(date.getTime())) {
      return format(date, 'yyyy-MM-dd')
    }

    return ''
  } catch (error) {
    console.error('Error converting date for input:', error)
    return ''
  }
}

export const DateFilter = <TData,>({
  column,
  columnHeader,
  setActiveFilters,
  inputVisible,
  navigate,
  disableUrlFilters,
  showAllOption = false,
}: FilterProps<TData>) => {
  const filterContainerRef = useRef<HTMLDivElement>(null)
  const [isInputVisible, setInputVisible] = useState(inputVisible)
  const [currentFilterValue, setCurrentFilterValue] = useState<
    [string, string]
  >(['', ''])
  const columnFilterValue = column.getFilterValue() as [string, string]

  useEffect(() => {
    if (columnFilterValue && columnFilterValue !== currentFilterValue) {
      const convertedValues: [string, string] = [
        convertToDateInputValue(columnFilterValue[0]),
        convertToDateInputValue(columnFilterValue[1]),
      ]
      if (
        convertedValues[0] !== currentFilterValue[0] ||
        convertedValues[1] !== currentFilterValue[1]
      ) {
        setCurrentFilterValue(convertedValues)
        setInputVisible(false)
      }
    }
  }, [columnFilterValue, currentFilterValue])

  const handleInputChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target
      const newValue = [...currentFilterValue] as [string, string]
      newValue[index] = value

      const formattedValue = newValue.map((date) => {
        if (!date) return ''
        try {
          if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return `${date}T00:00:00.000Z`
          }
          return new Date(date).toISOString()
        } catch (error) {
          console.error('Error formatting date:', error)
          return ''
        }
      }) as [string, string]

      column.setFilterValue(
        formattedValue[0] || formattedValue[1] ? formattedValue : undefined
      )
      setCurrentFilterValue(newValue)

      // Update URL parameters (only if URL filters are enabled)
      if (!disableUrlFilters) {
        if (newValue[0] || newValue[1]) {
          navigate({
            to: '.',
            search: (prev) => ({
              ...prev,
              [column.id]: formattedValue,
            }),
          })
        } else {
          removeFilter(column, setActiveFilters, navigate)
        }
      }
    }

  const handleOutsideClick = () => {
    const shouldRemoveFilter =
      !currentFilterValue[0] && !currentFilterValue[1] && !showAllOption
    if (shouldRemoveFilter) {
      removeFilter(column, setActiveFilters, navigate)
    } else {
      setInputVisible(false)
    }
  }

  useClickOutside(filterContainerRef, handleOutsideClick)

  const isAllSelected =
    showAllOption &&
    currentFilterValue[0] === '' &&
    currentFilterValue[1] === ''

  const getDisplayValue = () => {
    const [start, end] = currentFilterValue
    if (start && end) {
      return `${formatDateString(start)} - ${formatDateString(end)}`
    } else if (start) {
      return `After ${formatDateString(start)}`
    } else if (end) {
      return `Before ${formatDateString(end)}`
    } else if (isAllSelected) {
      return ALL_OPTION_VALUE
    }
    return ''
  }

  return (
    <RelativeWrap>
      <FilterHeaderButton
        onClick={() => setInputVisible((visible) => !visible)}>
        {columnHeader}
        {getDisplayValue() && (
          <RemoveFilterButton
            onClick={() => {
              removeFilter(column, setActiveFilters, navigate)
              setCurrentFilterValue(['', ''])
            }}>
            {getDisplayValue()}
            {!isAllSelected && <StyledCloseIcon />}
          </RemoveFilterButton>
        )}
      </FilterHeaderButton>
      {isInputVisible && (
        <FilterContainer
          ref={filterContainerRef}
          data-input-visible={isInputVisible ? 'true' : undefined}>
          <DateRangeContainer>
            <LabelAndInputContainer>
              <StyledLabel>After:</StyledLabel>
              <StyledInput
                type='date'
                value={currentFilterValue[0]}
                onChange={handleInputChange(0)}
                placeholder='Start date'
              />
            </LabelAndInputContainer>
            <LabelAndInputContainer>
              <StyledLabel>Before:</StyledLabel>
              <StyledInput
                type='date'
                value={currentFilterValue[1]}
                onChange={handleInputChange(1)}
                placeholder='End date'
              />
            </LabelAndInputContainer>
          </DateRangeContainer>
        </FilterContainer>
      )}
    </RelativeWrap>
  )
}
